// Loads and updates the organization's remembered import column mappings (file-header shape ->
// confirmed mapping), kept on the server so every member of the organization shares them.

import { useMemo } from 'react';
import { graphql } from '~/graphql';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import type { LearnedStore } from '~/pages/inventory-import/lib/autoMap';
import type { ColumnMapping } from '~/pages/inventory-import/lib/types';

const COLUMN_MAPPINGS_KEY = 'inventory-import-column-mappings';

const SellerImportColumnMappingsQuery = graphql(`
  query SellerImportColumnMappings {
    sellerImportColumnMappings {
      headerSignature
      mapping
    }
  }
`);

const RememberSellerImportColumnMappingMutation = graphql(`
  mutation RememberSellerImportColumnMapping(
    $headerSignature: String!
    $mapping: JSON!
  ) {
    rememberSellerImportColumnMapping(
      headerSignature: $headerSignature
      mapping: $mapping
    ) {
      headerSignature
    }
  }
`);

export function useColumnMappings() {
  const { data, isLoading, error } = useGraphqlQuery(
    SellerImportColumnMappingsQuery,
    [COLUMN_MAPPINGS_KEY]
  );

  const learned = useMemo<LearnedStore>(() => {
    const store: LearnedStore = {};
    for (const entry of data?.sellerImportColumnMappings ?? [])
      // The JSON scalar generates as Record<string, unknown>; narrow it to the mapping shape.
      store[entry.headerSignature] = entry.mapping as ColumnMapping;
    return store;
  }, [data]);

  return { learned, isLoading, error };
}

export function useRememberColumnMapping() {
  return useGraphqlMutation(RememberSellerImportColumnMappingMutation, [
    COLUMN_MAPPINGS_KEY,
  ]);
}

const ProposeSellerImportMappingMutation = graphql(`
  mutation ProposeSellerImportMapping(
    $columns: [ImportMappingColumnInput!]!
    $fields: [ImportMappingFieldInput!]!
  ) {
    proposeSellerImportMapping(columns: $columns, fields: $fields)
  }
`);

export function useProposeMapping() {
  return useGraphqlMutation(ProposeSellerImportMappingMutation);
}
